import { useCallback, useState } from "react"
import { toast } from "sonner"
import { errorMessage } from "@/lib/errors"

/**
 * The app-standard async submit wrapper: pending flag + success/error toasts.
 * Replaces the hand-rolled `setBusy/try/toast/finally` block.
 *
 * Returns true when `fn` resolved, so call sites can chain navigation:
 *   if (await run(() => save(args), { success: "Saved" })) navigate(...)
 */
export function usePendingAction() {
  const [pending, setPending] = useState(false)
  const run = useCallback(
    async (
      fn: () => Promise<unknown>,
      opts?: { success?: string; error?: string },
    ): Promise<boolean> => {
      setPending(true)
      try {
        await fn()
        if (opts?.success) toast.success(opts.success)
        return true
      } catch (err) {
        toast.error(
          errorMessage(err, (opts?.error ?? "Failed")),
        )
        return false
      } finally {
        setPending(false)
      }
    },
    [],
  )
  return { pending, run }
}
